import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import {
  SkeletonBlock,
  Pill,
  useBreakpoint
} from '@one-thd/sui-atomic-components';
import { Close } from '@one-thd/sui-icons';
import classNames from 'classnames';
import { ExperienceContext, useStore } from '@thd-nucleus/experience-context';
import { PickupContent, DeliveryContent } from './FulfillmentOptionSubcontentSelector';
import { useSearchModelData } from '../../hooks/useSearchModelData';
import {
  getFulfillmentOptionsViewModel,
  getFulfillmentAnalyticsByLabel,
  FULFILLMENT_VARIANTS
} from '../../utils/fulfillment-helpers';

const {
  func, oneOf, bool, shape, arrayOf, string
} = PropTypes;

const FulfillmentOptions = ({
  onFulfillmentChange,
  pillOverflowVariant,
  isDrawer,
  onChange,
  appliedDimensions: drawerAppliedDimensions
}) => {
  const { data, loading, responseLoading } = useSearchModelData();
  const { deliveryZip, isConsumerApp } = useContext(ExperienceContext);
  const { isLocalized } = useStore({ online: false });
  const { lesser: isSmScreen } = useBreakpoint('md', { ssr: true });

  const searchModel = data?.searchModel || {};
  const stores = searchModel?.metadata?.stores;
  const storeName = stores?.storeName;
  const dimensions = searchModel?.dimensions;

  // In drawer mode, use drawer's appliedDimensions for filters selection state.
  const appliedDimensions = isDrawer ? drawerAppliedDimensions : searchModel?.appliedDimensions;

  const {
    fulfillmentOptions,
    initialSelection
  } = getFulfillmentOptionsViewModel(dimensions, appliedDimensions);

  const [selectedOption, setSelectedOption] = useState(initialSelection);

  // Finding the dimension object from the response to send to callback (e.g., "GET IT FAST", "DELIVERY")
  const findSourceDimension = (sourceDimensionKey) => {
    return dimensions?.find(
      (dim) => (dim.label || '').toUpperCase() === sourceDimensionKey
    );
  };

  const handleFulfillmentChange = (option) => {
    const { label, sourceDimension, sourceRefinement } = option;

    setTimeout(() => {
      const analytics = getFulfillmentAnalyticsByLabel(label);
      const refinementWithAnalytics = {
        ...sourceRefinement,
        // Override refinement.label with the analytics value (e.g., 'Available in Store', 'Delivery', 'Free Delivery')
        // instead of the UI label (e.g., 'Pick up Today', 'By Tomorrow', 'Anytime') because the analytics
        // pipeline uses refinement.label to determine filterType (primary/secondary).
        // Applied in both direct and Drawer modes.
        label: analytics.value,
        dimensionName: 'GET IT FAST',
        analytics,
        redirectUrl: null,
        selected: null
      };

      if (isDrawer && typeof onChange === 'function') {
        // Drawer mode: pass data to reflect the All filters drawer UI, without navigating
        const realDimension = findSourceDimension(sourceDimension);
        onChange({ refinement: option.sourceRefinement, dimension: realDimension });
      } else {
        // Direct mode: immediate navigation
        onFulfillmentChange({ refinement: refinementWithAnalytics });
      }
    }, 0);
  };

  const handleTileChange = (option) => {
    const isDeselecting = option.label === selectedOption?.label;
    if (isDrawer) {
      setSelectedOption(isDeselecting ? null : option);
    } else if (!isDeselecting) {
      setSelectedOption(option);
    }
    handleFulfillmentChange(option);
  };

  useEffect(() => {
    setSelectedOption(initialSelection);
  }, [initialSelection?.label]);

  if (!isDrawer && !selectedOption && (!isLocalized || loading || responseLoading)) {
    return <SkeletonBlock height={7} data-testid="fulfillment-options-loading" />;
  }

  if (!fulfillmentOptions.length) {
    return null;
  }

  const isRefetching = !isDrawer && (loading || responseLoading);

  const pillsClasses = classNames(
    'sui-flex sui-items-center sui-gap-3 sm:sui-gap-4',
    {
      'sui-flex-nowrap sui-overflow-x-auto': pillOverflowVariant === 'scroll',
      'sui-flex-wrap': pillOverflowVariant === 'wrap',
    },
    selectedOption ? 'sui-shrink-0' : 'sui-flex-1'
  );

  return (
    <div
      id="fulfillment-options-container"
      data-component="FulfillmentOptions"
      className={classNames(
        'sui-flex sui-items-center sui-gap-4 sui-transition-opacity',
        { 'sui-opacity-50': isRefetching }
      )}
    >
      <div
        id="fulfillment-option-pills"
        data-testid="fulfillment-option-pills"
        className={pillsClasses}
      >
        {fulfillmentOptions.map((option) => {
          const LeadingIcon = option.iconOptions?.icon;
          const isSelected = option.label === selectedOption?.label;
          const isHidden = selectedOption && !isSelected;

          return (
            <Pill
              id={option.label}
              label={option.label}
              key={option.refinementKey || option.label}
              selected={isSelected}
              leadingSlot={LeadingIcon && <LeadingIcon color={option.iconOptions.color} />}
              trailingSlot={isSelected && <Close size="small" />}
              size={isSmScreen ? 'regular' : 'large'}
              onClick={() => handleTileChange(option)}
              data-testid={`fulfillment-option-${option.label}`}
              style={isHidden ? { display: 'none' } : undefined}
            />
          );
        })}
      </div>
      {selectedOption?.variant === FULFILLMENT_VARIANTS.PICKUP && (
        <PickupContent
          storeName={storeName}
          stores={stores}
          isDrawer={isDrawer}
        />
      )}
      {!isConsumerApp && (
        <DeliveryContent
          deliveryZip={deliveryZip}
          hide={selectedOption?.variant !== FULFILLMENT_VARIANTS.DELIVERY}
          isDrawer={isDrawer}
        />
      )}
    </div>
  );
};

FulfillmentOptions.propTypes = {
  onFulfillmentChange: func,
  pillOverflowVariant: oneOf(['wrap', 'scroll']),
  isDrawer: bool,
  onChange: func,
  appliedDimensions: arrayOf(shape({}))
};

FulfillmentOptions.defaultProps = {
  onFulfillmentChange: undefined,
  pillOverflowVariant: 'scroll',
  isDrawer: false,
  onChange: undefined,
  appliedDimensions: []
};

FulfillmentOptions.displayName = 'FulfillmentOptions';

export { FulfillmentOptions };